import React, { useState, useEffect, useRef } from "react"
import { GPSID, getConfig, getDirections, getGeoCode, getLicAddress, getLicName, loadGPSID, makeDirRenderer, makeMap, makeMarker } from "../../actions/utils"
import { Checkbox, Modal } from "antd";
import { flatten, get, isObject, last, size, orderBy } from "lodash";
import Row from '../../_shared/row';
import { Button } from "../../_shared/button";
import { SortableList } from '../../_shared/sortable-list';
import { useScreen } from "../../_shared/hooks/useScreen";
import { getMapState, getOptimizedRoutes, updateMapState } from '../../_shared/services/maps.service';
import { OrderStatus } from "../../models/order";

// https://www.google.com/maps/dir/33.93729,-106.85761/33.91629,-106.866761/33.98729,-106.85861//@34.0593359,-106.7131944,11z

const BUFFER = {
  mobile: 80,
  desktop: 145,
};

// --- stop grouping -----------------------------------------------------------
// A Google `place_id` identifies a physical PLACE, not a delivery. A storefront holding
// both a medical and a recreational licence produces two stops -- schedule.component.jsx
// setupMap emits one entry per licence -- that geocode to the SAME place_id. Using that
// id as the React key / react-beautiful-dnd draggableId collided: rbd keeps draggables in
// a flat object keyed by id, so the second registration silently replaced the first (that
// row would not drag), and React mis-reconciled the duplicate-keyed siblings (one row's
// content rendered in another row's slot -- the "duplicate" orders on card 276).
//
// Stops are now grouped by physical location: one group == one draggable == one stop
// number, rendering one sub-row per licence. The group key is unique by construction, so
// the collision cannot recur, and both licences at an address share one stop number.
// Only the SAME address groups -- never stops that are merely near each other (card 300,
// see getGroupKey).
const MAP_ROLE = { start: 'start', end: 'end' };

const GROUP_STYLE = {
  borderLeft: '3px solid rgba(0,0,0,0.35)',
  borderRadius: 3,
  background: 'rgba(0,0,0,0.04)',
  paddingLeft: 8,
  paddingRight: 4,
  paddingTop: 2,
  paddingBottom: 2,
};

const SUBROW_STYLE = {
  borderTop: '1px dashed rgba(0,0,0,0.18)',
  marginTop: 3,
  paddingTop: 3,
};

const normalizeAddress = a => `${a || ''}`.trim().toLowerCase().replace(/\s*,\s*/g, ', ').replace(/\s+/g, ' ');

// Card 300 -- a place_id is only an address's identity when Google matched the WHOLE address.
// When it cannot place the house number it answers with something coarser, flagged
// `partial_match`: the street ("330 / 155 / 445 North Federal Boulevard, Denver" all came
// back as "N Federal Blvd, Denver", types ['route']), the zip, or the town. Every such stop
// then shares that street's/town's place_id, and grouping on it merged three different
// licences at three different addresses into one stop on CO prod's Denver West, 9/30.
// So the place_id groups only an exact, address-level match -- which is what still keeps a
// med + rec pair whose unit is only written differently ("Unit B" / "Suite B") on one stop.
// Anything else falls back to the address text below.
const ADDRESS_LEVEL_TYPES = ['street_address', 'premise', 'subpremise'];
const isAddressLevel = m => (get(m, 'types') || []).some(t => ADDRESS_LEVEL_TYPES.includes(t));
const isExactAddressMatch = m => !get(m, 'partial_match') && isAddressLevel(m);

// Card 300, round 2 -- the pin, Show Directions and Open in Google Maps all use this point.
// Some licence addresses carry a leading direction Google does not use: "330 North Federal
// Boulevard, Denver" is "330 Federal Blvd" to Google, so it answers with only the street
// ("N Federal Blvd", types ['route']), whose mid-point is 2.7 miles north of the store.
// When the first answer is coarser than an address, geocode once more without the
// direction, and keep that answer only if it is an exact match for the same house number
// and zip on a street with no other direction ("45 N Main Street, Hutchinson" without the
// N comes back as "45 Main St S", the other half of the street).
const LEADING_DIRECTION = /^(\s*\d+[a-z]?)\s+(north|south|east|west|n|s|e|w)\.?\s+(?=\S)/i;
const ROUTE_DIRECTION = /^(n|s|e|w|ne|nw|se|sw)\s|\s(n|s|e|w|ne|nw|se|sw)$/i;
const digits = v => `${v || ''}`.replace(/\D/g, '');
const componentOf = (m, type) => get((get(m, 'address_components') || []).find(c => (c.types || []).includes(type)), 'short_name', '');

const withoutLeadingDirection = address => {
  const [street, ...rest] = `${address || ''}`.split(',');
  const match = street.match(LEADING_DIRECTION);
  if (!match) return null;
  return {
    address: [street.replace(LEADING_DIRECTION, '$1 '), ...rest].join(','),
    number: digits(match[1]),
    direction: match[2][0].toUpperCase(),
    zip: (`${address}`.match(/\b(\d{5})(?:-\d{4})?\s*$/) || [])[1],
  };
};

const confirmsRetry = (result, retry) => {
  if (!isExactAddressMatch(result)) return false;
  if (digits(componentOf(result, 'street_number')) !== retry.number) return false;
  const postal = componentOf(result, 'postal_code');
  if (retry.zip && postal && postal !== retry.zip) return false;
  const routeDirection = (componentOf(result, 'route').match(ROUTE_DIRECTION) || []).slice(1).find(d => !!d);
  return !routeDirection || routeDirection.toUpperCase() === retry.direction;
};

const geocodeAddress = async address => {
  const mark = await getGeoCode({ address });
  if (isAddressLevel(mark)) return mark;

  const retry = withoutLeadingDirection(address);
  if (!retry) return mark;

  try {
    const better = await getGeoCode({ address: retry.address });
    return confirmsRetry(better, retry) ? better : mark;
  } catch (err) {
    return mark;
  }
};

// Identity of the physical location a marker sits at. Prefixed so the namespaces can
// never overlap, and so a configured start/end address never merges into a real stop.
const getGroupKey = (m, i) => {
  const role = get(m, '__mapRole');
  if (role) return `role:${role}`;
  const placeId = get(m, 'place_id');
  if (placeId && isExactAddressMatch(m)) return `place:${placeId}`;
  const address = normalizeAddress(get(m, 'address'));
  if (address) return `addr:${address}`;
  return `idx:${i}`;
};

// Identity of one licence WITHIN a group -- never derived from the address.
const getMemberKey = (m, groupKey, i) => {
  const ln = get(m, 'ln');
  return ln ? `ln:${ln}` : `${groupKey}:${i}`;
};

// Collapses a flat marker list into location groups, preserving first-appearance order.
// Exactly one group object is created per distinct key, so group keys are unique by
// construction -- there is no code path that can emit the same key twice.
const buildGroups = (markers) => {
  const byKey = {};
  const groups = [];

  (markers || []).forEach((m, i) => {
    const key = getGroupKey(m, i);
    if (!byKey[key]) {
      byKey[key] = { key, members: [] };
      groups.push(byKey[key]);
    }
    byKey[key].members.push(m);
  });

  return groups;
};

// state.markers is kept grouped-contiguous so the persisted `order` matches what is drawn.
const groupSort = markers => flatten(buildGroups(markers).map(g => g.members));

// Card 300 -- Open in Google Maps and Show Directions take ONE point per stop number, in list
// order: a grouped stop (a med + rec pair at one address) used to go to Google once per
// licence. Pass the live markers only; each stop is then its first live licence.
const onePerStop = markers => buildGroups(markers).map(g => g.members[0]);

// Saved mapstate rows have carried `ln` since Dec 2025; older rows only have address+name,
// so fall back to the legacy predicate rather than resetting their ordering.
const findSavedEntry = (mapaddrs, m) => {
  const ln = get(m, 'ln');
  if (ln) {
    const byLn = (mapaddrs || []).find(ma => !!get(ma, 'ln') && ma.ln == ln);
    if (byLn) return byLn;
  }
  return (mapaddrs || []).find(ma => ma.address == m.address && ma.name == m.name);
};

// Matches an entry of the `addresses` prop back to its marker. Licence number first: two
// stops at one address have identical address strings, and stage holds a pair whose
// strings differ only by a stray space before a comma, which `==` gets wrong both ways.
const matchesEntry = (m, addr) => {
  const ln = get(addr, 'ln');
  if (ln) return get(m, 'ln') == ln;
  const role = get(addr, '__mapRole');
  if (role) return get(m, '__mapRole') === role;
  return m.address == (addr.address || addr) && m.name == addr.name;
};

export const Map = props => {
  const { isMobile, windowSize } = useScreen();

  const hasStart = getConfig('map.startAddress');
  const hasEnd = getConfig('map.endAddress');

  const addresses = [
    hasStart ? { ...hasStart, __mapRole: MAP_ROLE.start, name: get(hasStart, 'name', get(hasStart, 'address')) } : null,
    ...props.addresses,
    hasEnd ? { ...hasEnd, __mapRole: MAP_ROLE.end, name: get(hasEnd, 'name', get(hasEnd, 'address')) } : null,
  ].filter(f => !!get(f, 'address'));

  const [state, setState] = useState({
    map: undefined,
    dirRenderer: undefined,
    markers: [],
    
    mapstate: undefined,
    hasLoaded: false,
    dirEnabled: false,
  })
  
  const dirEnabled = state.dirEnabled;

  const mapref = useRef();

  const loadAddresses = async () => {
    try {
      const markers = (await Promise.all(addresses.map(async (addr, i) => {
        try {
          const address = get(addr, 'address', addr);
          const mark = await geocodeAddress(address);
          
          const marker = makeMarker({
            map: state.map,
            position: mark.geometry.location,
          })
    
          return {
            ...(isObject(addr) ? addr : { address: addr }),
            ...mark,
            marker,
          };
        } catch (err) {
          console.error(err);
          return null;
        }
      }))).filter(f => !!f);
  
      state.map.setCenter(get(last(markers), 'geometry.location'));
      state.map.setZoom(10);
  
      const dirRenderer = makeDirRenderer({ suppressMarkers: true });
      dirRenderer.setMap(state.map);
  
      setState(s => ({ ...s, markers: markers, dirRenderer }));
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  const makeUrl = () => {
    const getUrlLoc = l => {
      return `${get(l, 'geometry.location.lat')()},${get(l, 'geometry.location.lng')()}`
    }

    return `https://www.google.com/maps/dir//${onePerStop(state.markers.filter(f => !f.deleted)).map(m => getUrlLoc(m)).join('/')}`;////@${getUrlLoc(state.markers[0])},11z`;
  }

  // SortableList is handed location GROUPS, so a reorder comes back as groups: flatten
  // them to keep state.markers a flat, grouped-contiguous marker list.
  const onSort = ord => {
    setState(s => {
      const markers = flatten((ord || []).map(g => get(g, 'members', [])));

      if (size(markers) !== size(s.markers)) {
        console.error('[map] reorder dropped markers, ignoring', size(markers), size(s.markers));
        return s;
      }

      return { ...s, markers };
    })
  }

  const openUrl = () => {
    window.open(makeUrl(), '_blank');
  }

  const optimize = async () => {
    const resp = await getOptimizedRoutes(state.markers.map(m => ({ name: m.name, address: m.address })))
    console.log('optimize', state, resp);
  }

  const deleteMarker = m => {
    setState(s => ({ ...s, markers: s.markers.map(f => f.place_id === m.place_id && m.ln == f.ln ? { ...f, deleted: true } : f) }))
  }

  const addMarkerBack = m => {
    setState(s => ({ ...s, markers: s.markers.map(f => f.place_id === m.place_id && m.ln == f.ln ? { ...f, deleted: false } : f) }))
  }

  const loadMapState = async () => {
    if (props.id) {
      const mapstate = await getMapState(props.id);
      setState(s => ({ ...s, mapstate }));
    }
  }

  const loadDirections = async () => {
    if (dirEnabled) {
      state.dirRenderer.setMap(state.map);
      const validmarkers = state.markers.filter(f => !f.deleted);
      let origin;
      let destination;
      let waypoints = [];
  
      const possel = 'geometry.location';
  
      onePerStop(validmarkers).map((mark, i, stops) => {
        if (i === 0) {
          origin = get(mark, possel);
        } else if (i === stops.length - 1) {
          destination = get(mark, possel);
        } else {
          waypoints.push({ location: get(mark, possel) });
        }
      })

      // Card 300 -- Google needs an origin AND a destination. With one point left (one stop,
      // which can be a med + rec pair) or none, it threw and the last route stayed drawn, so
      // hide it as unticking Show Directions does. A configured start/end is a point like any
      // stop, so start + one stop still draws.
      if (!origin || !destination) {
        state.dirRenderer.setMap(null);
        return;
      }
  
      const directions = await getDirections({
        origin,
        destination,
        waypoints,
        travelMode: 'DRIVING',
      })
  
      state.dirRenderer.setDirections(directions)
    } else {
      state.dirRenderer.setMap(null);
    }
  }

  useEffect(() => {
    const validStatus = [
      OrderStatus.orderReceived, 
      OrderStatus.orderApproved, 
      OrderStatus.inTransit,
      OrderStatus.orderPickedUp,
    ]
    
    if (size(state.markers) && state.mapstate && !state.hasLoaded) { //loadstate
      const mapaddrs = get(state.mapstate, 'data.addresses', []);

      setState(s => {
        const markers = orderBy(s.markers.map((m, i) => { 
          const findmatch = findSavedEntry(mapaddrs, m);
          const deleted = get(findmatch, 'deleted', false);
          const order = get(findmatch, 'order', i);
          const hasValidStatus = (m.status || []).find(ms => validStatus.includes(ms)) != undefined;
          
          return {
            ...m,
            order,
            deleted: deleted || !hasValidStatus,
          }
        }), 'order');

        return {
          ...s, 
          hasLoaded: true,
          markers: groupSort(markers),
        }
      });
    }

    if (props.id && state.mapstate && state.hasLoaded) { //savestate
      const addrs = addresses.map(addr => {
        const address = addr.address || addr;

        const order = state.markers.findIndex(m => matchesEntry(m, addr));
        const marker = order > -1 ? state.markers[order] : undefined;

        return {
          address,
          deleted: get(marker, 'deleted'),
          order,
          name: addr.name,
          ln: get(addr, 'ln'),
        }
      })

      updateMapState({ id: props.id, date: props.date, data: { addresses: addrs } });
    }

    if (size(state.markers) && state.hasLoaded) {
      loadDirections(); 
    }
  }, [state.markers, state.mapstate, state.hasLoaded, dirEnabled]);

  useEffect(() => {
    setState(s => ({
      ...s,
      map: makeMap(mapref.current),
    }))

    loadMapState();
  }, []);

  useEffect(() => {
    if (!!state.map) { loadAddresses(); }
  }, [state.map]);

  const getHeight = (mult) => {
    return (windowSize.innerHeight * mult) - (isMobile ? BUFFER.mobile : BUFFER.desktop);
  }

  // One draggable per physical location. The stop number is the group's position among
  // groups that still have at least one live licence, so labels come out 1, 2, 3, ... by
  // construction -- no findIndex, and no need for the old "bump a repeated label by +1"
  // band-aid, which only ever existed to paper over the place_id collision.
  let stop = 0;
  const items = buildGroups(state.markers).map(group => {
    const { key, members } = group;
    const isActive = members.some(m => !m.deleted);
    const label = isActive ? `${++stop}` : 'deleted';
    const isGrouped = size(members) > 1;

    members.forEach(m => {
      if (!m.marker) { return; }
      if (m.deleted) {
        m.marker.setMap(null);
      } else {
        m.marker.setMap(state.map);
        m.marker.setLabel(label);
      }
    });

    // One sub-row per licence, each keyed off the LICENCE, never off the address.
    const rows = members.map((m, mi) => (
      <Row
        key={getMemberKey(m, key, mi)}
        style={{ width: '100%', opacity: m.deleted ? 0.4 : 1, ...(mi > 0 ? SUBROW_STYLE : {}) }}
      >
        <span>{`${m.deleted ? 'deleted' : label}: ${m.name}`}</span>
        <div style={{ marginLeft: 'auto' }}>
          <Button style={{ marginLeft: 5 }} icon={m.deleted ? "plus" : "delete"} size="small" onClick={m.deleted ? () => addMarkerBack(m) : () => deleteMarker(m)} />
        </div>
      </Row>
    ));

    return {
      key,
      members,
      disabled: !isActive,
      // A single-licence stop renders exactly the markup it did before, so routes with no
      // shared addresses are unchanged. Only a real group gets the shared container.
      content: isGrouped
        ? <div style={{ width: '100%', ...GROUP_STYLE }}>{rows}</div>
        : rows[0]
    }
  });

  return (
    <div>
      <Row style={{ marginBottom: 5 }}>
        <Button onClick={openUrl} icon="export" title="Open in Google Maps" style={{ marginRight: 10, }} />
        {/* <Button onClick={optimize} icon="dashboard" title="Optimize Route" style={{ marginRight: 10, }} /> */}
        <Checkbox checked={state.dirEnabled} onChange={e => setState(s => ({ ...s, dirEnabled: !s.dirEnabled }))}>Show Directions</Checkbox>
      </Row>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ width: isMobile ? '100%' : '25%', paddingRight: isMobile ? 0 : 10, height: getHeight(isMobile ? 0.45 : 1), overflowY: 'scroll' }}>
          <SortableList items={items} onSort={onSort} />
        </div>
        <div id="MAP" ref={mapref} style={{ height: getHeight(isMobile ? 0.55 : 1), width: isMobile ? '100%' : '75%' }} />
      </div>
    </div>
  )
}

export const MapModal = props => {
  const { addresses } = props;

  const onClose = () => {
    props.onClose();
  }

  return (
    <Modal 
      visible={!!addresses} 
      width={'100%'} 
      height={window.innerHeight} 
      onOk={onClose} 
      onCancel={onClose}
      style={{ top: 0 }}
    >
      {!!addresses && <Map {...props} />}
    </Modal>
  )
}